<template>
  <div class="container-fluid py-4">
    <div class="d-flex justify-content-center align-items-center mb-4">
      <Button class="start-0" :label="$t('Back')" route-name="CamProduction" />
      <Brand width="150" height="150" />
    </div>

    <div class="row justify-content-center">
      <div class="col-12 col-lg-7">
        <div v-if="uiState === 'loading'" class="card shadow-sm border-0 rounded-custom p-5 text-center">
          <div class="spinner-border text-violet" role="status" />
          <p class="small text-muted mt-3 mb-0">{{ $t("Preparing the CAM tool...") }}</p>
        </div>

        <div v-else-if="uiState === 'error'" class="card shadow-sm border-0 rounded-custom p-4">
          <div class="alert alert-danger mb-0">{{ pageError }}</div>
        </div>

        <template v-else>
          <div v-if="successMessage" class="alert alert-success">
            {{ successMessage }}
          </div>

          <CamProductionForm
            v-if="isEditMode || !savedLog"
            :form="form"
            :materials="materials"
            :operator-name="displayOperatorName"
            :logged-at-label="previewLoggedAt"
            :can-submit="canSubmit"
            :is-saving="isSaving"
            :submit-error="submitError"
            :title="formTitle"
            :description="formDescription"
            :submit-label="submitButtonLabel"
            @update-field="updateField"
            @submit="saveLog"
          />

          <template v-if="savedLog">
            <RecordSummary :log="savedLog" :is-edit-mode="isEditMode" />

            <div class="card shadow-sm border-0 rounded-custom mt-4">
              <div class="card-body p-4 p-lg-5">
                <PhotoManager
                  :log="savedLog"
                  :show-photo-editor="showPhotoEditor"
                  :use-desktop-upload-instead="useDesktopUploadInstead"
                  :photo-upload-token="photoUploadToken"
                  :photo-upload-url="photoUploadUrl"
                  :photo-upload-expires-at="photoUploadExpiresAt"
                  :is-generating-qr="isGeneratingQr"
                  :is-removing-photo="isRemovingPhoto"
                  @update:use-desktop-upload-instead="useDesktopUploadInstead = $event"
                  @generate-photo-qr="generatePhotoQr"
                  @replace-photo="startPhotoReplacement"
                  @remove-photo="removeSavedPhoto"
                  @desktop-uploaded="handleDesktopUploaded"
                  @desktop-error="submitError = $event"
                />

                <div class="d-flex flex-wrap gap-2">
                  <button
                    v-if="!isEditMode"
                    class="btn btn-outline-violet text-uppercase"
                    @click="resetFlow"
                  >
                    {{ $t("New registration") }}
                  </button>
                  <button
                    v-if="!isEditMode && savedLog?.photo_media_uuid"
                    class="btn btn-violet text-uppercase"
                    @click="router.push({ name: 'CamProduction' })"
                  >
                    {{ $t("Back to table") }}
                  </button>
                  <button
                    v-if="isEditMode"
                    class="btn btn-outline-violet text-uppercase"
                    @click="router.push({ name: 'CamProduction' })"
                  >
                    {{ $t("Back to list") }}
                  </button>
                </div>
              </div>
            </div>
          </template>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, reactive, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import Brand from "@/components/Brand.vue";
import Button from "@/components/general/Button.vue";
import CamProductionForm from "@/components/cam/CamProductionForm.vue";
import PhotoManager from "@/components/cam/production/PhotoManager.vue";
import RecordSummary from "@/components/cam/production/RecordSummary.vue";
import { useCamPhotoStatusPolling } from "@/composables/useCamPhotoStatusPolling";
import {
  createLog,
  createPhotoUpload,
  getForm,
  removePhoto,
  showLog,
  updateLog,
} from "@/use/repositories/camProduction";
import { firstErrorMessage } from "@/use/utilities/camProduction/firstErrorMessage";

export default {
  name: "CamProduction",
  components: {
    Brand,
    Button,
    CamProductionForm,
    PhotoManager,
    RecordSummary,
  },
  setup() {
    const formatDateTime = (value = new Date()) => new Date(value).toLocaleString("it-IT");
    const route = useRoute();
    const router = useRouter();
    const i18n = useI18n();
    const store = useStore();
    const uiState = ref("loading");
    const pageError = ref("");
    const submitError = ref("");
    const successMessage = ref("");
    const materials = ref([]);
    const isSaving = ref(false);
    const isGeneratingQr = ref(false);
    const isRemovingPhoto = ref(false);
    const savedLog = ref(null);
    const photoUploadToken = ref("");
    const photoUploadExpiresAt = ref(null);
    const useDesktopUploadInstead = ref(false);
    const photoEditorEnabled = ref(false);
    const previewLoggedAt = ref(formatDateTime());

    const form = reactive({
      milledElementsCount: null,
      materialId: null,
      machineName: "",
      notes: "",
    });

    const isEditMode = computed(() => Boolean(route.params.id));
    const editableLogId = computed(() => route.params.id?.toString() || savedLog.value?.uuid || null);
    const canSubmit = computed(() => Number.isInteger(form.milledElementsCount) && form.milledElementsCount > 0);

    const currentUserName = computed(() => {
      const user = store.state.user ?? {};
      const fullName = [user.first_name, user.last_name].filter(Boolean).join(" ").trim();

      return fullName || user.name || user.email || "-";
    });

    const displayOperatorName = computed(() => savedLog.value?.user_name || currentUserName.value);
    const formTitle = computed(() =>
      isEditMode.value ? i18n.t("Edit milled elements record") : i18n.t("Milled elements registration"),
    );
    const formDescription = computed(() =>
      isEditMode.value
        ? i18n.t("Update the record data and, if needed, manage the photo as well.")
        : i18n.t("Enter the essential production data."),
    );
    const submitButtonLabel = computed(() =>
      isEditMode.value ? i18n.t("Update record") : i18n.t("Save record"),
    );
    const photoUploadUrl = computed(() =>
      photoUploadToken.value
        ? new URL(
          router.resolve({
            name: "CamProductionPhotoUpload",
            params: { token: photoUploadToken.value },
          }).href,
          window.location.origin,
        ).toString()
        : "",
    );
    const showPhotoEditor = computed(
      () => Boolean(savedLog.value) && (photoEditorEnabled.value || !savedLog.value?.photo_media_uuid),
    );

    const applyLogToForm = (log) => {
      form.milledElementsCount = log?.milled_elements_count ?? null;
      form.materialId = log?.material_id ?? null;
      form.machineName = log?.machine_name ?? "";
      form.notes = log?.notes ?? "";
      previewLoggedAt.value = formatDateTime(log?.logged_at || new Date());
    };

    const generatePhotoQr = async () => {
      if (!savedLog.value?.uuid) return;

      submitError.value = "";
      isGeneratingQr.value = true;

      try {
        const { data } = await createPhotoUpload(savedLog.value.uuid);
        photoUploadToken.value = data?.token ?? "";
        photoUploadExpiresAt.value = data?.expires_at ?? null;
      } catch (error) {
        submitError.value = firstErrorMessage(
          error,
          i18n.t("Unable to generate the CAM photo link for this record."),
        );
      } finally {
        isGeneratingQr.value = false;
      }
    };

    const preparePhotoEditor = async ({ force = false } = {}) => {
      if (!savedLog.value?.uuid) return;

      if (!force && savedLog.value.photo_media_uuid) {
        photoEditorEnabled.value = false;
        return;
      }

      photoEditorEnabled.value = true;
      await generatePhotoQr();
    };

    const syncSavedLogStatus = async () => {
      if (!savedLog.value?.uuid) return;

      try {
        const { log } = await showLog(savedLog.value.uuid, false);
        savedLog.value = log ?? savedLog.value;
        applyLogToForm(savedLog.value);

        if (savedLog.value?.photo_media_uuid) {
          photoUploadToken.value = "";
          photoUploadExpiresAt.value = null;
          useDesktopUploadInstead.value = false;
          photoEditorEnabled.value = false;
          successMessage.value = i18n.t("Photo saved successfully.");
          stopPhotoStatusPolling();
        }
      } catch (error) {
        console.error(error);
      }
    };

    const { stop: stopPhotoStatusPolling } = useCamPhotoStatusPolling(
      () => [savedLog.value?.uuid, savedLog.value?.photo_media_uuid, showPhotoEditor.value],
      syncSavedLogStatus,
      {
        maxAttempts: 24,
        onTimeout: () => {
          submitError.value = i18n.t("Automatic photo check stopped. Regenerate the link if needed.");
        },
      },
    );

    const loadForm = async () => {
      uiState.value = "loading";
      pageError.value = "";

      try {
        const { data } = await getForm();
        materials.value = data?.materials ?? [];

        if (isEditMode.value) {
          const { log } = await showLog(route.params.id?.toString());
          savedLog.value = log ?? null;
          applyLogToForm(savedLog.value);
          await preparePhotoEditor();
        }

        uiState.value = "ready";
      } catch (error) {
        pageError.value = firstErrorMessage(error, i18n.t("Unable to load the CAM tool."));
        uiState.value = "error";
      }
    };

    const persistLog = async () => {
      const payload = {
        milled_elements_count: form.milledElementsCount,
        material_id: form.materialId,
        machine_name: form.machineName || null,
        notes: form.notes || null,
      };

      if (isEditMode.value && editableLogId.value) {
        return updateLog(editableLogId.value, payload);
      }

      return createLog(payload);
    };

    const saveLog = async () => {
      submitError.value = "";
      successMessage.value = "";

      if (!canSubmit.value) {
        submitError.value = i18n.t("The milled elements count is required.");
        return;
      }

      isSaving.value = true;

      try {
        const { log } = await persistLog();

        savedLog.value = log ?? null;
        applyLogToForm(savedLog.value);
        photoUploadToken.value = "";
        photoUploadExpiresAt.value = null;
        useDesktopUploadInstead.value = false;

        if (isEditMode.value) {
          successMessage.value = i18n.t("CAM record updated successfully.");
        } else {
          successMessage.value = i18n.t("CAM record saved. You can now manage the photo.");
          await preparePhotoEditor();
        }
      } catch (error) {
        submitError.value = firstErrorMessage(error, i18n.t("An error occurred while saving the CAM record."));
      } finally {
        isSaving.value = false;
      }
    };

    const startPhotoReplacement = async () => {
      successMessage.value = "";
      await preparePhotoEditor({ force: true });
    };

    const handleDesktopUploaded = (log) => {
      savedLog.value = log ?? savedLog.value;
      applyLogToForm(savedLog.value);
      photoUploadToken.value = "";
      photoUploadExpiresAt.value = null;
      useDesktopUploadInstead.value = false;
      photoEditorEnabled.value = false;
      submitError.value = "";
      successMessage.value = savedLog.value?.photo_media_uuid
        ? i18n.t("Photo updated successfully.")
        : i18n.t("Photo saved successfully.");
    };

    const removeSavedPhoto = async () => {
      if (!savedLog.value?.uuid) return;

      submitError.value = "";
      successMessage.value = "";
      isRemovingPhoto.value = true;

      try {
        const { log } = await removePhoto(savedLog.value.uuid);
        savedLog.value = log ?? savedLog.value;
        applyLogToForm(savedLog.value);
        successMessage.value = i18n.t("Photo removed successfully.");
        await preparePhotoEditor({ force: true });
      } catch (error) {
        submitError.value = firstErrorMessage(error, i18n.t("Unable to remove the CAM photo."));
      } finally {
        isRemovingPhoto.value = false;
      }
    };

    const resetFlow = () => {
      form.milledElementsCount = null;
      form.materialId = null;
      form.machineName = "";
      form.notes = "";
      previewLoggedAt.value = formatDateTime();
      savedLog.value = null;
      photoUploadToken.value = "";
      photoUploadExpiresAt.value = null;
      useDesktopUploadInstead.value = false;
      photoEditorEnabled.value = false;
      successMessage.value = "";
      submitError.value = "";
      stopPhotoStatusPolling();
    };

    const updateField = (field, value) => {
      form[field] = value;
    };

    onMounted(() => {
      loadForm();
    });

    return {
      uiState,
      pageError,
      submitError,
      successMessage,
      materials,
      form,
      displayOperatorName,
      previewLoggedAt,
      canSubmit,
      isSaving,
      savedLog,
      isEditMode,
      formTitle,
      formDescription,
      submitButtonLabel,
      showPhotoEditor,
      isGeneratingQr,
      isRemovingPhoto,
      useDesktopUploadInstead,
      photoUploadToken,
      photoUploadUrl,
      photoUploadExpiresAt,
      saveLog,
      generatePhotoQr,
      resetFlow,
      updateField,
      startPhotoReplacement,
      removeSavedPhoto,
      handleDesktopUploaded,
      router,
    };
  },
};
</script>
